import {Map as ImmutableMap} from 'immutable';
import {
  FbLogo,
  FbLogoLarge,
  GoogleLogo,
  GoogleLogoLarge,
} from '@packages/systems/ui/icons';
import {SUBSCRIPTION_STATUS_ENUM} from '@packages/systems/commerce/constants';

export const INTEGRATIONS = {
  FACEBOOK: {
    code: 'facebook',
    name: 'Facebook',
    logo: FbLogo,
    logoLarge: FbLogoLarge,
  },
  GOOGLE: {
    code: 'google',
    name: 'Google',
    logo: GoogleLogo,
    logoLarge: GoogleLogoLarge,
  },
} as const;

/**
 * A union type of all integration names, as derived from the "name" key
 * of all objects in INTEGRATIONS.
 */
export type IntegrationName =
  (typeof INTEGRATIONS)[keyof typeof INTEGRATIONS]['name'];

// @ts-expect-error - TS2322 - Type 'Map<string, string>' is not assignable to type 'Map<"country", string>'.
export const DEFAULT_BUSINESS_ADDRESS_COUNTRY: ImmutableMap<'country', string> =
  ImmutableMap({
    country: 'US',
  });

// Commerce menu workspace names:
export const COMMERCE_SETTINGS = 'COMMERCE_SETTINGS';
export const PRODUCT_MANAGER = 'PRODUCT_MANAGER';
export const CATEGORY_MANAGER = 'CATEGORY_MANAGER';

export const CommerceWorkspaces = {
  COMMERCE_SETTINGS,
  PRODUCT_MANAGER,
  CATEGORY_MANAGER,
} as const;

export type CommerceWorkspace = keyof typeof CommerceWorkspaces | '';

// Product Manager View names
export const PRODUCTS_LIST = 'PRODUCTS_LIST';

export const ProductManagerViews = {
  PRODUCTS_LIST,
} as const;

export type ProductManagerView = keyof typeof ProductManagerViews;

// Category Manager View names
export const CATEGORIES_LIST = 'CATEGORIES_LIST';

export const CategoryManagerViews = {
  CATEGORIES_LIST,
} as const;

export type CategoryManagerView = keyof typeof CategoryManagerViews;

export const NETWORK_STATUSES = {
  LOADING: 'LOADING',
  SUCCEEDED: 'SUCCEEDED',
  FAILED: 'FAILED',
  INIT: 'INIT',
} as const;

export const CONFIRMATION_STATUSES: {
  CONFIRMED: 'CONFIRMED';
  REQUESTED: 'REQUESTED';
  REQUIRED: 'REQUIRED';
  INIT: 'INIT';
} = {
  CONFIRMED: 'CONFIRMED',
  REQUESTED: 'REQUESTED',
  REQUIRED: 'REQUIRED',
  INIT: 'INIT',
};

export const CMS_STATES = {
  READY: 'ready',
  MISSING: 'missing',
  BROKEN: 'broken',
  CONFLICT: 'conflict',
  OUTDATED: 'outdated',
} as const;

export const NO_STATUS_FILTER = 'all';

export const CLIENT_ORDER_STATES = {
  UNFULFILLED: 'unfulfilled',
  FULFILLED: 'fulfilled',
  REFUNDED: 'refunded',
  DISPUTED: 'disputed',
} as const;

export const SERVER_ORDER_STATES = {
  UNFULFILLED: 'unfulfilled',
  FULFILLED: 'fulfilled',
  REFUNDED: 'refunded',
  DISPUTED: 'disputed',
  DISPUTELOST: 'dispute-lost',
} as const;

export const SERVER_TO_CLIENT_STATES = {
  [SERVER_ORDER_STATES.UNFULFILLED]: CLIENT_ORDER_STATES.UNFULFILLED,
  [SERVER_ORDER_STATES.FULFILLED]: CLIENT_ORDER_STATES.FULFILLED,
  [SERVER_ORDER_STATES.REFUNDED]: CLIENT_ORDER_STATES.REFUNDED,
  [SERVER_ORDER_STATES.DISPUTED]: CLIENT_ORDER_STATES.DISPUTED,
  [SERVER_ORDER_STATES.DISPUTELOST]: CLIENT_ORDER_STATES.DISPUTED,
} as const;

export const DEFAULT_ORDERS_SORT_COLUMN = 'acceptedOn';
export const DEFAULT_ORDERS_SORT_DIRECTION = 'DESC';

export const ORDERS_REQUEST_PAGE_SIZE = 25;

export type ConfirmationStatus = keyof typeof CONFIRMATION_STATUSES;
export type NetworkStatus = keyof typeof NETWORK_STATUSES;
export type CMSStates = (typeof CMS_STATES)[keyof typeof CMS_STATES] | null;
export type OrderStateChangeValues =
  | 'fulfilled'
  | 'refunded'
  | 'unfulfilled'
  | 'error';
export type ServerOrderStatus =
  (typeof SERVER_ORDER_STATES)[keyof typeof SERVER_ORDER_STATES];
export type OrderStatusFilter =
  | (typeof CLIENT_ORDER_STATES)[keyof typeof CLIENT_ORDER_STATES]
  | typeof NO_STATUS_FILTER;

export type SubscriptionStatusFilter =
  | (typeof SUBSCRIPTION_STATUS_ENUM)[keyof typeof SUBSCRIPTION_STATUS_ENUM]
  | typeof NO_STATUS_FILTER;

export const COMMERCE_GENERAL_SETTINGS = 'general';
export const COMMERCE_PAYMENT_SETTINGS = 'payment';
export const COMMERCE_PAYMENT_STRIPE_SETTINGS = 'payment-stripe';
export const COMMERCE_PAYMENT_PAYPAL_SETTINGS = 'payment-paypal';
export const COMMERCE_CHECKOUT_SETTINGS = 'checkout';
export const COMMERCE_SHIPPING_SETTINGS = 'shipping';
export const COMMERCE_TAXES_SETTINGS = 'taxes';
export const COMMERCE_AUTO_TAX_SETTINGS = 'autoTax';
export const COMMERCE_SHIPPING_ZONE_SETTINGS = 'shippingZone';
export const COMMERCE_INTEGRATIONS_SETTINGS = 'integrations';
export const COMMERCE_INVENTORY_SYNC_SETTINGS = 'inventorySync';
export const COMMERCE_SHIPPING_PROVIDERS_SETTINGS = 'shippingProviders';
export const COMMERCE_NOTIFICATIONS_SETTINGS = 'notifications';
export const COMMERCE_EMAIL_TYPE_SETTINGS = 'emailTypeSettings';

export const COMMERCE_SETTINGS_ID = 'commerceSettings';
export const addressValidationFields = [
  'country',
  'state',
  'postalCode',
] as const;
export const paymentGatewaySupportValidationFields = [
  'country',
  'currency',
] as const;

export const REST_OF_THE_WORLD = {
  code: 'restOfTheWorld',
  name: 'Rest of the World',
} as const;

export const COMMERCE_SETTINGS_SHIPPING_METHOD_FORM_ID =
  'commerceSettingsShippingMethod';

export const DEFAULT_PAYMENT_GATEWAY_SUPPORT = {
  stripe: {
    currencyConflict: false,
    countryConflict: false,
  },
  paypal: {
    currencyConflict: false,
    countryConflict: false,
  },
} as const;

export const GETTING_STARTED_TO_DO_STATUSES = {
  ADD_BUSINESS_ADDRESS: 'hasBusinessAddress',
  REVIEW_CURRENCY_SETTINGS: 'hasReviewedCurrencySettings',
  ADD_PRODUCT: 'hasProducts',
  ADD_SHIPPING_METHOD: 'hasShippingMethods',
  REVIEW_TAX_SETTINGS: 'hasCheckedTaxSettings',
  ADD_CART: 'hasAddedCart',
  GO_TO_PRODUCT_PAGE: 'hasVisitedProductPage',
  GO_TO_CHECKOUT_PAGE: 'hasVisitedCheckoutPage',
  ADD_PAYMENT_PROVIDER: 'hasPaymentProcessor',
  ADD_HOSTING: 'hasCorrectHostingPlan',
  ENABLE_SSL: 'hasSSLEnabled',
  CHECK_EMAIL_SETTINGS: 'hasCheckedEmailSettings',
  ENABLE_CHECKOUT: 'hasCheckoutEnabled',
} as const;

// No SVG, because mail clients don't like them
export const COMMERCE_SUPPORTED_IMAGE_TYPES = [
  'jpeg',
  'jpg',
  'png',
  'webp',
  'gif',
  'avif',
] as const;

// These are different amount format options for the CommerceAmountFormatSelectSection
// located in Settings > Ecommerce > General > Currency that determines the defaultCurrencyFormat setting
// Different options depend on what decimals look like for a certain currency

export const NO_DECIMAL_OPTIONS = [
  {code: ',', name: '10,000'},
  {code: '.', name: '10.000'},
  {code: '`', name: '10`000'},
  {code: '٬', name: '10٬000'},
  {code: ' ', name: '10 000'},
  {code: '', name: '10000'},
] as const;

export const ONE_DECIMAL_OPTIONS = [
  {code: ',.', name: '10,000.0'},
  {code: '.,', name: '10.000,0'},
  {code: '٬,', name: '10٬000,0'},
  {code: '`.', name: '10`000.0'},
  {code: ' ,', name: '10 000,0'},
  {code: ' .', name: '10 000.0'},
  {code: ' $', name: '10 000$0'},
  {code: ',', name: '10000,0'},
  {code: '.', name: '10000.0'},
] as const;

export const TWO_DECIMAL_OPTIONS = [
  {code: ',.', name: '10,000.00'},
  {code: '.,', name: '10.000,00'},
  {code: '٬,', name: '10٬000,00'},
  {code: '`.', name: '10`000.00'},
  {code: ' ,', name: '10 000,00'},
  {code: ' .', name: '10 000.00'},
  {code: ' $', name: '10 000$00'},
  {code: ',', name: '10000,00'},
  {code: '.', name: '10000.00'},
] as const;

export const THREE_DECIMAL_OPTIONS = [
  {code: ',.', name: '10,000.000'},
  {code: '.,', name: '10.000,000'},
  {code: '٬,', name: '10٬000,000'},
  {code: '`.', name: '10`000.000'},
  {code: ' ,', name: '10 000,000'},
  {code: ' .', name: '10 000.000'},
  {code: ' $', name: '10 000$000'},
  {code: ',', name: '10000,000'},
  {code: '.', name: '10000.000'},
] as const;

export const NO_DISCOUNT_ID_YET = 'NO_DISCOUNT_ID_YET';
export const PRODUCT_INVENTORY_FIELD_META_SLUG = 'product-inventory';
export const PRODUCT_QUANTITY_FIELD_META_SLUG = 'product-quantity';
